<template>
    <div class="dashboard-container inv-page">

        <!-- ============================================================
             MODALS (unchanged)
             ============================================================ -->
        <b-modal id="modal-received-history" scrollable :title="`RECEIVED HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveReceivedHistory">
                        <b-form-group label="Add Received Quantity">
                            <b-input type="number" v-model="newReceivedQty" placeholder="Enter quantity" required></b-input>
                        </b-form-group>
                        <b-button type="submit" variant="success" size="sm">Add Received</b-button>
                    </b-form>
                </b-card>
                <b-table striped hover :items="receivedHistory" :fields="historyFields" class="mt-3">
                    <template #cell(timestamp)="data">{{ formatDate(data.value) }}</template>
                    <template #cell(actions)="data">
                        <b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('received', data.item.id)"><b-icon icon="trash"></b-icon></b-button>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <b-modal id="modal-inventory-image" size="lg" hide-footer centered title="Product Image">
            <div class="text-center">
                <b-img :src="currentFullSizeImage" fluid alt="Full size product image" style="max-height: 70vh; object-fit: contain;" />
                <div class="mt-3">
                    <strong>{{ currentImageProductName }}</strong>
                    <div class="text-muted small">{{ currentImageProductSku }}</div>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-inventory-image')">Close</b-button>
            </template>
        </b-modal>

        <b-modal id="modal-add-to-damages" scrollable title="ADD ITEM TO DAMAGES" size="lg" @hidden="reset_damages_form" no-close-on-backdrop>
            <div v-if="selectedItem">
                <b-card class="mb-3">
                    <b-row><b-col><strong>Product:</strong> {{ selectedItem.product_name }}</b-col><b-col><strong>SKU:</strong> {{ selectedItem.product_sku }}</b-col></b-row>
                    <b-row class="mt-2"><b-col><strong>Current Location:</strong> {{ packet.locsubsdict[selectedItem.locsub] }}</b-col><b-col><strong>Current Stock:</strong> {{ selectedItem.in_stock }}</b-col></b-row>
                </b-card>
                <b-form>
                    <b-row>
                        <b-col md="6"><b-form-group label="Source Location"><b-form-select v-model="newDamageItem.source_locsub" :options="availableLocations" required></b-form-select></b-form-group></b-col>
                        <b-col md="6"><b-form-group label="Target Location"><b-form-select v-model="newDamageItem.target_locsub" :options="availableLocations" required></b-form-select></b-form-group></b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6"><b-form-group label="Damage Type"><b-form-select v-model="newDamageItem.damage_type" :options="damageTypeOptions" required></b-form-select></b-form-group></b-col>
                        <b-col md="6"><b-form-group label="Damage Grade"><b-form-select v-model="newDamageItem.damage_grade" :options="damageGradeOptions" required></b-form-select></b-form-group></b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6"><b-form-group label="Original Price"><b-form-input type="number" step="0.01" v-model="newDamageItem.original_price"></b-form-input></b-form-group></b-col>
                        <b-col md="6"><b-form-group label="Selling Price"><b-form-input type="number" step="0.01" v-model="newDamageItem.selling_price" required></b-form-input></b-form-group></b-col>
                    </b-row>
                    <b-form-group label="Damage Description"><b-form-textarea v-model="newDamageItem.damage_description" rows="3"></b-form-textarea></b-form-group>
                    <b-form-group label="Damage Images">
                        <b-form-file v-model="damageImages" multiple accept="image/*" @change="handleImageUpload"></b-form-file>
                        <div v-if="imagePreviews.length > 0" class="mt-3 d-flex flex-wrap">
                            <div v-for="(preview, index) in imagePreviews" :key="index" class="mr-2 mb-2">
                                <img :src="preview" style="max-width: 100px; max-height: 100px; object-fit: cover;">
                                <b-button size="sm" variant="outline-danger" @click="removeImage(index)" class="mt-1" style="width: 100%;">Remove</b-button>
                            </div>
                        </div>
                    </b-form-group>
                </b-form>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="reset_damages_form" :disabled="isSubmitting">Cancel</b-button>
                <b-button variant="danger" @click="save_damaged_item" :disabled="isSubmitting">
                    <b-spinner v-if="isSubmitting" small class="mr-2"></b-spinner>
                    {{ isSubmitting ? 'Saving...' : 'Add to AS IS' }}
                </b-button>
            </template>
        </b-modal>

        <b-modal id="modal-missing-history" scrollable :title="`MISSING HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveMissingHistory">
                        <b-form-group label="Add Missing Quantity"><b-input type="number" v-model="newMissingQty" required></b-input></b-form-group>
                        <b-button type="submit" variant="warning" size="sm">Add Missing</b-button>
                    </b-form>
                </b-card>
                <b-table striped hover :items="missingHistory" :fields="historyFields" class="mt-3">
                    <template #cell(timestamp)="data">{{ formatDate(data.value) }}</template>
                    <template #cell(actions)="data"><b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('missing', data.item.id)"><b-icon icon="trash"></b-icon></b-button></template>
                </b-table>
            </div>
        </b-modal>

        <b-modal id="modal-damages-history" scrollable :title="`DAMAGES HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveDamagesHistory">
                        <b-form-group label="Add Damages Quantity"><b-input type="number" v-model="newDamagesQty" required></b-input></b-form-group>
                        <b-button type="submit" variant="danger" size="sm">Add Damages</b-button>
                    </b-form>
                </b-card>
                <b-table striped hover :items="damagesHistory" :fields="historyFields" class="mt-3">
                    <template #cell(timestamp)="data">{{ formatDate(data.value) }}</template>
                    <template #cell(actions)="data"><b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('damages', data.item.id)"><b-icon icon="trash"></b-icon></b-button></template>
                </b-table>
            </div>
        </b-modal>

        <b-modal id="modal-sold-history" scrollable :title="`SOLD HISTORY - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card>
                    <b-form @submit.prevent="saveSoldHistory">
                        <b-form-group label="Add Sold Quantity"><b-input type="number" v-model="newSoldQty" required></b-input></b-form-group>
                        <b-button type="submit" variant="info" size="sm">Add Sold</b-button>
                    </b-form>
                </b-card>
                <b-table striped hover :items="soldHistory" :fields="historyFields" class="mt-3">
                    <template #cell(timestamp)="data">{{ formatDate(data.value) }}</template>
                    <template #cell(actions)="data"><b-button size="sm" variant="outline-danger" @click="deleteHistoryItem('sold', data.item.id)"><b-icon icon="trash"></b-icon></b-button></template>
                </b-table>
            </div>
        </b-modal>

        <b-modal id="modal-edit-received" title="Edit Received Quantity" @ok="saveEditedReceived"><b-form-group label="Received Quantity"><b-input type="number" v-model="editReceivedQty" required></b-input></b-form-group></b-modal>
        <b-modal id="modal-edit-missing" title="Edit Missing Quantity" @ok="saveEditedMissing"><b-form-group label="Missing Quantity"><b-input type="number" v-model="editMissingQty" required></b-input></b-form-group></b-modal>
        <b-modal id="modal-edit-damages" title="Edit Damages Quantity" @ok="saveEditedDamages"><b-form-group label="Damages Quantity"><b-input type="number" v-model="editDamagesQty" required></b-input></b-form-group></b-modal>
        <b-modal id="modal-edit-sold" title="Edit Sold Quantity" @ok="saveEditedSold"><b-form-group label="Sold Quantity"><b-input type="number" v-model="editSoldQty" required></b-input></b-form-group></b-modal>

        <b-modal id="modal-reserved-invoices" scrollable :title="`RESERVED INVOICES - ${selectedItem ? selectedItem.product_name : ''}`" size="lg" hide-footer>
            <div v-if="selectedItem">
                <b-card class="mb-3">
                    <b-row><b-col><strong>SKU:</strong> {{ selectedItem.product_sku }}</b-col><b-col><strong>Location:</strong> {{ packet.locsubsdict[selectedItem.locsub] }}</b-col></b-row>
                    <b-row class="mt-2"><b-col><strong>Total Reserved:</strong> {{ selectedItem.total_reserved }}</b-col><b-col><strong>Total Invoices:</strong> {{ selectedItem.total_invoices }}</b-col></b-row>
                </b-card>
                <b-table striped hover :items="reservedInvoices" :fields="reservedInvoiceFields" class="mt-3">
                    <template #cell(invoice_id)="data">
                        <a :href="`http://inventory.myfurnituremecca.com/#/sales/viewsale/${data.value}/`" target="_blank" class="invoice-link">{{ data.value }} <b-icon icon="box-arrow-up-right" scale="0.8"></b-icon></a>
                    </template>
                    <template #cell(invoice_date)="data">{{ formatDate(data.value) }}</template>
                    <template #cell(invoice_completed)="data"><b-badge :variant="data.value ? 'success' : 'warning'">{{ data.value ? 'Completed' : 'Pending' }}</b-badge></template>
                </b-table>
                <div v-if="reservedInvoices.length === 0" class="text-center text-muted p-4"><b-icon icon="file-earmark-x" font-scale="2"></b-icon><p class="mt-2">No reserved invoices found for this item</p></div>
            </div>
        </b-modal>

        <!-- ============================================================
             HERO
             ============================================================ -->
        <header class="rsv2-hero">
            <div class="rsv2-hero-grid">
                <div>
                    <div class="rsv2-hero-title">
                        INVENTORY BY LOCATION
                        <span v-if="displayedItems.length > 0" class="rsv2-count-pill">{{ displayedItems.length }}</span>
                    </div>
                    <div class="rsv2-hero-tags">
                        <span class="rsv2-tag is-orange"><b-icon-geo-alt class="mr-1" /> {{ locationName || 'All Locations' }}</span>
                        <span class="rsv2-tag is-teal"><b-icon-box-seam class="mr-1" /> Showing {{ displayedItems.length }} of {{ items.length }}</span>
                    </div>
                </div>
                <div class="rsv2-hero-qr">
                    <b-icon-box-seam font-scale="2" />
                    <div>
                        <div class="rsv2-qr-title">Live Inventory</div>
                        <div class="rsv2-qr-sub">Real-time tracking across locations</div>
                    </div>
                </div>
            </div>
        </header>

        <!-- ============================================================
             VIEW TOGGLE
             ============================================================ -->
        <section class="rsv2-card rsv2-viewcard">
            <div class="rsv2-view-row">
                <div class="rsv2-view-block">
                    <small class="rsv2-label"><b-icon-table class="mr-1" /> REPORT VIEW</small>
                    <div class="rsv2-pillgroup">
                        <button class="rsv2-pill-btn" :class="{ 'is-active': view_mode === 'product' }" @click="setViewMode('product')">
                            <b-icon-grid-3x3-gap class="mr-1" /> Product Cards
                        </button>
                        <button class="rsv2-pill-btn" :class="{ 'is-active': view_mode === 'list' }" @click="setViewMode('list')">
                            <b-icon-list-ul class="mr-1" /> List View
                        </button>
                    </div>
                </div>
                <div class="rsv2-view-block">
                    <small class="rsv2-label"><b-icon-arrow-repeat class="mr-1" /> ACTIONS</small>
                    <div>
                        <b-button variant="outline-secondary" class="inv-refresh-btn" @click="get_inventory_for_location">
                            <b-icon-arrow-repeat class="mr-1" /> Refresh
                        </b-button>
                    </div>
                </div>
            </div>
        </section>

        <!-- ============================================================
             FILTERS
             ============================================================ -->
        <b-card no-body class="inv-filters-card mb-3">
            <b-card-body class="py-3">
                <div v-if="isMobile" class="d-flex justify-content-between align-items-center mb-2">
                    <b-button size="md" variant="outline-primary" @click="filtersVisible = !filtersVisible">
                        <b-icon-funnel-fill class="mr-1" />{{ filtersVisible ? "Hide Filters" : "Show Filters" }}
                    </b-button>
                </div>
                <b-collapse :visible="!isMobile || filtersVisible">
                    <div class="filter-grid">
                        <div class="filter-item">
                            <label class="filter-label"><b-icon-geo-alt class="mr-1" />LOCATION</label>
                            <b-form-select :options="locations" v-model="location_id" @change="get_inventory_for_location" size="md" class="filter-select">
                                <template #first><b-form-select-option :value="null" :disabled="true">PICK A LOCATION</b-form-select-option></template>
                            </b-form-select>
                        </div>
                        <div class="filter-item" v-if="items.length > 0">
                            <label class="filter-label"><b-icon-diagram-3 class="mr-1" />SUBLOCATION</label>
                            <b-form-select :options="packet.locsubs.filter(d => d.location_id == location_id)" v-model="picked_locsub" size="md" class="filter-select">
                                <template #first><b-form-select-option :value="null">All Sublocations</b-form-select-option></template>
                            </b-form-select>
                        </div>
                        <div class="filter-item" v-if="items.length > 0">
                            <label class="filter-label"><b-icon-tag class="mr-1" />CATEGORY</label>
                            <b-form-select :options="categoryOptions" v-model="picked_category" size="md" class="filter-select">
                                <template #first><b-form-select-option :value="null">All Categories</b-form-select-option></template>
                            </b-form-select>
                        </div>
                        <div class="filter-item" v-if="items.length > 0">
                            <label class="filter-label"><b-icon-search class="mr-1" />SEARCH</label>
                            <b-form-input v-model="search_txt" size="md" class="filter-input" placeholder="Product name or SKU..."></b-form-input>
                        </div>
                        <div class="filter-item" v-if="items.length > 0">
                            <label class="filter-label">&nbsp;</label>
                            <b-button :variant="hide_zero_in_stock ? 'warning' : 'success'" size="md" class="w-100 toggle-btn" @click="hide_zero_in_stock = !hide_zero_in_stock">
                                <b-icon-eye-slash v-if="!hide_zero_in_stock" class="mr-1" /><b-icon-eye v-else class="mr-1" />
                                {{ hide_zero_in_stock ? 'Show 0 In-Stock' : 'Hide 0 In-Stock' }}
                            </b-button>
                        </div>
                        <div class="filter-item" v-if="items.length > 0 && packet.userDetails.userrole == 'admin'">
                            <label class="filter-label">&nbsp;</label>
                            <b-button variant="info" size="md" class="w-100 download-btn" @click="downloadExcel"><b-icon-download class="mr-1" />Download Excel</b-button>
                        </div>
                    </div>
                </b-collapse>
            </b-card-body>
        </b-card>

        <!-- ============================================================
             LOADING
             ============================================================ -->
        <div v-if="items_being_loaded" class="rsv2-card rsv2-loading-inline">
            <b-spinner variant="primary" type="grow" /><span>Loading inventory…</span>
        </div>

        <div v-else>
            <!-- KPI -->
            <section class="rsv2-kpi-grid" v-if="items.length > 0">
                <div class="rsv2-kpi is-total">
                    <div class="rsv2-kpi-icon"><b-icon-graph-up /></div>
                    <div class="rsv2-kpi-body">
                        <div class="rsv2-kpi-value">{{ formatCurrency(totalInventoryValue) }}</div>
                        <div class="rsv2-kpi-label">Total Inventory Value</div>
                    </div>
                </div>
                <div class="rsv2-kpi is-count">
                    <div class="rsv2-kpi-icon"><b-icon-box-seam /></div>
                    <div class="rsv2-kpi-body">
                        <div class="rsv2-kpi-value">{{ totalInStock }}</div>
                        <div class="rsv2-kpi-label">Units In Stock</div>
                    </div>
                </div>
                <div class="rsv2-kpi is-layaway">
                    <div class="rsv2-kpi-icon"><b-icon-clipboard-check /></div>
                    <div class="rsv2-kpi-body">
                        <div class="rsv2-kpi-value">{{ totalReserved }}</div>
                        <div class="rsv2-kpi-label">Reserved For Orders</div>
                    </div>
                </div>
                <div class="rsv2-kpi" :class="stockHealthPercent >= 75 ? 'is-count' : (stockHealthPercent >= 50 ? 'is-layaway' : 'is-refund')">
                    <div class="rsv2-kpi-icon"><b-icon-box /></div>
                    <div class="rsv2-kpi-body">
                        <div class="rsv2-kpi-value">{{ totalAvailable }}</div>
                        <div class="rsv2-kpi-label">Available · {{ stockHealthPercent }}% Health</div>
                    </div>
                </div>
            </section>

            <div v-if="items.length == 0" class="rsv2-card rsv2-empty">
                <div class="rsv2-empty-icon"><b-icon-exclamation-triangle font-scale="2.4" /></div>
                <h3>No inventory for this location</h3>
                <p>Pick a different location or refresh.</p>
            </div>

            <div v-else>
                <div class="products-header">
                    <h2 class="section-title">Inventory</h2>
                    <span class="section-subtitle">Showing {{ displayedItems.length }} of {{ items.length }} items</span>
                </div>

                <!-- =====================================================
                     PRODUCT-CARD VIEW
                     ===================================================== -->
                <div v-if="view_mode === 'product'" class="products-grid">
                    <div v-for="(item, idx) in displayedItems" :key="'p-' + idx" v-if="allowed(item)" class="pc-card">
                        <div class="inv-pc-sum">
                            <div class="inv-pc-img" @click="showFullInventoryImage(item)">
                                <button v-if="getProductImage(item)" type="button" class="pc-img-btn">
                                    <img :src="getProductImage(item)" :alt="item.product_name || item.product_sku" @error="handleImageError($event, item)" />
                                </button>
                                <div v-else class="pc-img-empty"><b-icon-image font-scale="3" /><span>No Image</span></div>
                            </div>
                            <div class="pc-prod">
                                <div class="pc-name" :title="item.product_name">{{ item.product_name }}</div>
                                <div class="pc-prod-meta"><span class="pc-sku">SKU <b>{{ item.product_sku }}</b></span></div>
                                <div class="pc-badges">
                                    <span class="pc-badge is-dark">{{ item.category || 'Uncategorized' }}</span>
                                    <span v-if="item.in_stock > 0" class="pc-badge is-good">In stock · {{ item.in_stock }}</span>
                                    <span v-else class="pc-badge is-danger">Out of stock</span>
                                </div>
                                <div class="pc-prod-taxonomy"><span class="pc-tax-item"><b-icon-geo-alt class="mr-1" />{{ packet.locsubsdict[item.locsub] || '—' }}</span></div>
                            </div>
                            <div class="inv-pc-price">
                                <div class="pc-sum-k">Price</div>
                                <div class="pc-big is-price" v-if="packet.userDetails.userrole == 'admin'">${{ (item.selling_price || 0).toFixed(2) }}</div>
                                <div class="pc-sum-v" v-else>{{ packet.locsubsdict[item.locsub] }}</div>
                            </div>
                            <div class="inv-pc-stock">
                                <div class="pc-sum-k">Stock breakdown</div>
                                <div class="inv-stock-rows">
                                    <button type="button" class="inv-stock-row" @click.stop="show_reserved_invoices(item)">
                                        <span class="inv-stock-ico is-res"><b-icon-clipboard-check /></span>
                                        <span class="inv-stock-k">Reserved</span>
                                        <span class="inv-stock-v">{{ item.reserved || 0 }}</span>
                                    </button>
                                    <div class="inv-stock-row">
                                        <span class="inv-stock-ico is-av"><b-icon-box /></span>
                                        <span class="inv-stock-k">Available</span>
                                        <span class="inv-stock-v">{{ item.available || 0 }}</span>
                                    </div>
                                    <div class="inv-stock-row">
                                        <span class="inv-stock-ico is-in"><b-icon-box-seam /></span>
                                        <span class="inv-stock-k">In Stock</span>
                                        <span class="inv-stock-v">{{ item.in_stock || 0 }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="inv-pc-actions">
                                <template v-if="(packet.userDetails.userrole == 'admin' || packet.userDetails.userrole == 'officemanager') && !item.is_out_of_stock">
                                    <template v-if="item.edit_in_stock">
                                        <b-input size="sm" v-model="item.in_stock_fake" style="width: 80px;"></b-input>
                                        <b-button size="sm" variant="success" @click="save_in_stock(item)">SAVE</b-button>
                                        <b-button size="sm" variant="secondary" @click="cancel_save_in_stock(item)">X</b-button>
                                    </template>
                                    <template v-else>
                                        <button type="button" class="pc-icon-btn" @click="edit_in_stock(item)" v-b-tooltip.hover.top title="Edit stock"><b-icon-pencil-square /></button>
                                    </template>
                                </template>
                                <b-button v-if="canMove" variant="outline-secondary" class="inv-move-btn" @click.stop="move_items(item)"><b-icon-arrow-left-right class="mr-1" /> Move</b-button>
                                <b-button v-if="canAsIs" variant="danger" class="inv-asis-btn" @click.stop="show_add_to_damages(item)"><b-icon-exclamation-triangle class="mr-1" /> AS IS</b-button>
                            </div>
                            <div v-if="item.move_items" class="inv-move-form">
                                <div class="move-row"><strong>FROM:</strong> {{ packet.locsubsdict[item.locsub] }}</div>
                                <div class="move-row"><strong>TO:</strong><b-form-select size="sm" :options="packet.locsubs" v-model="item.to_locsub" class="mt-1"></b-form-select></div>
                                <div class="move-row"><strong>QTY:</strong><b-form-input size="sm" type="number" v-model="item.move_quantity" style="width: 80px; display: inline-block;" class="ml-1"></b-form-input></div>
                                <div class="move-actions">
                                    <b-button size="sm" variant="secondary" @click="cancel_move(item)">Cancel</b-button>
                                    <b-button size="sm" variant="success" @click="submit_move_wrapper(item)">Submit</b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- =====================================================
                     LIST VIEW (exact Product Manager layout)
                     ===================================================== -->
                <div v-else-if="view_mode === 'list'" class="pc-list inv-pc-list">
                    <!-- Column header -->
                    <div class="pc-head" :style="listGridStyle">
                        <div class="pc-head-cell">
                            <span class="pc-head-static">Product</span>
                            <button type="button" class="pc-head-sort" @click="sortby('product_name')">
                                Name<b-icon v-if="activeSortKey === 'product_name'" :icon="sort_asc ? 'arrow-up' : 'arrow-down'"></b-icon>
                            </button>
                            <button type="button" class="pc-head-sort" @click="sortby('product_sku')">
                                SKU<b-icon v-if="activeSortKey === 'product_sku'" :icon="sort_asc ? 'arrow-up' : 'arrow-down'"></b-icon>
                            </button>
                        </div>
                        <div class="pc-head-cell is-num">
                            <button type="button" class="pc-head-sort" @click="sortby('selling_price')">
                                Price<b-icon v-if="activeSortKey === 'selling_price'" :icon="sort_asc ? 'arrow-up' : 'arrow-down'"></b-icon>
                            </button>
                        </div>
                        <div class="pc-head-cell is-num"><span class="pc-head-static">Stock</span></div>
                        <div class="pc-head-cell is-num"><span class="pc-head-static">Reserved / Available</span></div>
                        <div class="pc-head-cell"></div>
                    </div>

                    <!-- Rows -->
                    <article
                        v-for="(item, idx) in displayedItems"
                        :key="'l-' + idx"
                        v-if="allowed(item)"
                        class="pc-card inv-pc-card"
                        :class="{
                            'is-open': invIsOpen(item),
                            'is-out': (item.in_stock || 0) === 0,
                            'is-low': (item.in_stock || 0) > 0 && (item.available || 0) <= 2
                        }"
                    >
                        <div class="pc-sum" :style="listGridStyle">
                            <!-- Image -->
                            <div class="pc-img inv-pc-img-list">
                                <button v-if="getProductImage(item)" type="button" class="pc-img-btn" @click="showFullInventoryImage(item)">
                                    <img :src="getProductImage(item)" :alt="item.product_name || item.product_sku" @error="handleImageError($event, item)" />
                                </button>
                                <div v-else class="pc-img-empty"><span class="pc-ico"><b-icon-image /></span></div>
                            </div>

                            <!-- Product -->
                            <div class="pc-prod">
                                <div class="pc-name" :title="item.product_name">{{ item.product_name }}</div>
                                <div class="pc-prod-meta">
                                    <span class="pc-sku">SKU <b>{{ item.product_sku }}</b></span>
                                </div>
                                <div class="pc-badges">
                                    <span class="pc-badge is-dark">{{ item.category || 'Uncategorized' }}</span>
                                    <span v-if="item.in_stock > 0" class="pc-badge is-good">In stock</span>
                                    <span v-else class="pc-badge is-danger">Out of stock</span>
                                    <span v-if="item.available === 0 && item.in_stock > 0" class="pc-badge is-warn">All reserved</span>
                                </div>
                                <div class="pc-prod-taxonomy">
                                    <span class="pc-tax-item"><span class="pc-ico"><b-icon-geo-alt /></span>{{ packet.locsubsdict[item.locsub] || '—' }}</span>
                                </div>
                            </div>

                            <!-- Price -->
                            <div class="pc-sum-cell is-num inv-list-price-cell">
                                <div class="pc-sum-k">Price</div>
                                <div class="pc-big is-price" v-if="packet.userDetails.userrole == 'admin'">${{ (item.selling_price || 0).toFixed(2) }}</div>
                                <div class="pc-sum-v" v-else>{{ packet.locsubsdict[item.locsub] }}</div>
                            </div>

                            <!-- Stock -->
                            <div class="pc-sum-cell pc-sum-stock">
                                <div class="pc-stock-head">
                                    <span class="pc-stock-title">Stock</span>
                                </div>
                                <div class="pc-stock-row">
                                    <span class="pc-stock-k">IN STOCK</span>
                                    <span class="pc-stock-v is-all">{{ item.in_stock || 0 }}</span>
                                </div>
                                <div class="pc-stock-row">
                                    <span class="pc-stock-k">AVAILABLE</span>
                                    <span class="pc-stock-v is-available">{{ item.available || 0 }}</span>
                                </div>
                            </div>

                            <!-- Reserved / Available -->
                            <div class="pc-sum-cell pc-sum-totals">
                                <div class="pc-totals-head">
                                    <span class="pc-totals-title">Totals</span>
                                    <span class="pc-totals-hint">tap for history</span>
                                </div>
                                <button type="button" class="pc-totals-row" @click="show_reserved_invoices(item)">
                                    <span class="pc-totals-ico is-ord"><span class="pc-ico"><b-icon-clipboard-check /></span></span>
                                    <span class="pc-totals-k">Reserved</span>
                                    <span class="pc-totals-v is-ord">{{ item.reserved || 0 }}</span>
                                    <span class="pc-totals-arrow"><span class="pc-ico"><b-icon-chevron-right /></span></span>
                                </button>
                                <button type="button" class="pc-totals-row" @click="show_received_history(item)">
                                    <span class="pc-totals-ico is-recv"><span class="pc-ico"><b-icon-box-arrow-in-down /></span></span>
                                    <span class="pc-totals-k">Received</span>
                                    <span class="pc-totals-v is-recv">{{ item.received || 0 }}</span>
                                    <span class="pc-totals-arrow"><span class="pc-ico"><b-icon-chevron-right /></span></span>
                                </button>
                                <button type="button" class="pc-totals-row" @click="show_sold_history(item)">
                                    <span class="pc-totals-ico is-sold"><span class="pc-ico"><b-icon-cash-stack /></span></span>
                                    <span class="pc-totals-k">Sold</span>
                                    <span class="pc-totals-v is-sold">{{ item.sold || 0 }}</span>
                                    <span class="pc-totals-arrow"><span class="pc-ico"><b-icon-chevron-right /></span></span>
                                </button>
                            </div>

                            <!-- Actions -->
                            <div class="pc-sum-actions">
                                <template v-if="(packet.userDetails.userrole == 'admin' || packet.userDetails.userrole == 'officemanager') && !item.is_out_of_stock">
                                    <template v-if="item.edit_in_stock">
                                        <b-input size="sm" v-model="item.in_stock_fake" style="width: 70px;"></b-input>
                                        <button type="button" class="pc-icon-btn" @click="save_in_stock(item)"><b-icon-check2 /></button>
                                        <button type="button" class="pc-icon-btn" @click="cancel_save_in_stock(item)"><b-icon-x /></button>
                                    </template>
                                    <template v-else>
                                        <button type="button" class="pc-icon-btn" @click="edit_in_stock(item)" v-b-tooltip.hover.top title="Edit stock"><b-icon-pencil-square /></button>
                                    </template>
                                </template>
                                <button v-if="canMove" type="button" class="pc-icon-btn" @click.stop="move_items(item)" v-b-tooltip.hover.top title="Move inventory"><b-icon-arrow-left-right /></button>
                                <button v-if="canAsIs" type="button" class="pc-icon-btn is-danger" @click.stop="show_add_to_damages(item)" v-b-tooltip.hover.top title="Send to AS IS"><b-icon-exclamation-triangle /></button>
                                <button type="button" class="pc-icon-btn pc-toggle" @click="invToggle(item)">
                                    <b-icon :icon="invIsOpen(item) ? 'chevron-up' : 'chevron-down'" />
                                </button>
                            </div>
                        </div>

                        <!-- Details -->
                        <div v-if="invIsOpen(item)" class="pc-details">
                            <section class="pc-sec">
                                <h4 class="pc-sec-title">Main</h4>
                                <div class="pc-row"><span class="pc-k">Category</span><span class="pc-v">{{ item.category || '—' }}</span></div>
                                <div class="pc-row"><span class="pc-k">Sublocation</span><span class="pc-v">{{ packet.locsubsdict[item.locsub] || '—' }}</span></div>
                                <div class="pc-row"><span class="pc-k">SKU</span><span class="pc-v">{{ item.product_sku }}</span></div>
                            </section>
                            <section class="pc-sec">
                                <h4 class="pc-sec-title">Pricing</h4>
                                <div class="pc-row"><span class="pc-k">Selling price</span><span class="pc-v">${{ (item.selling_price || 0).toFixed(2) }}</span></div>
                                <div class="pc-row"><span class="pc-k">Wholesale</span><span class="pc-v">${{ (item.wholesale_price || 0).toFixed(2) }}</span></div>
                                <div class="pc-row"><span class="pc-k">Landed cost</span><span class="pc-v">${{ (item.landed_cost || 0).toFixed(2) }}</span></div>
                            </section>
                            <section class="pc-sec">
                                <h4 class="pc-sec-title">Inventory</h4>
                                <div class="pc-row"><span class="pc-k">In stock</span><span class="pc-v">{{ item.in_stock || 0 }}</span></div>
                                <div class="pc-row"><span class="pc-k">Reserved</span><span class="pc-v">{{ item.reserved || 0 }}</span></div>
                                <div class="pc-row"><span class="pc-k">Available</span><span class="pc-v" :class="(item.available || 0) > 0 ? 'is-good' : 'is-bad'">{{ item.available || 0 }}</span></div>
                                <div class="pc-row"><span class="pc-k">Missing</span><span class="pc-v" :class="{ 'is-bad': (item.missing || 0) > 0 }">{{ item.missing || 0 }}</span></div>
                                <div class="pc-row"><span class="pc-k">Damages</span><span class="pc-v">{{ item.damages || 0 }}</span></div>
                            </section>
                            <section class="pc-sec">
                                <h4 class="pc-sec-title">History <small>tap for details</small></h4>
                                <button type="button" class="pc-row pc-row--btn" @click="show_received_history(item)">
                                    <span class="pc-k">Received</span>
                                    <span class="pc-v">{{ item.received || 0 }}</span>
                                </button>
                                <button type="button" class="pc-row pc-row--btn" @click="show_missing_history(item)">
                                    <span class="pc-k">Missing</span>
                                    <span class="pc-v">{{ item.missing || 0 }}</span>
                                </button>
                                <button type="button" class="pc-row pc-row--btn" @click="show_damages_history(item)">
                                    <span class="pc-k">Damages</span>
                                    <span class="pc-v">{{ item.damages || 0 }}</span>
                                </button>
                                <button type="button" class="pc-row pc-row--btn" @click="show_sold_history(item)">
                                    <span class="pc-k">Sold</span>
                                    <span class="pc-v">{{ item.sold || 0 }}</span>
                                </button>
                            </section>
                            <section class="pc-sec" v-if="item.move_items">
                                <h4 class="pc-sec-title">Move</h4>
                                <div class="pc-row"><span class="pc-k">FROM</span><span class="pc-v">{{ packet.locsubsdict[item.locsub] }}</span></div>
                                <div class="pc-row">
                                    <span class="pc-k">TO</span>
                                    <b-form-select size="sm" :options="packet.locsubs" v-model="item.to_locsub" class="mt-1"></b-form-select>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">QTY</span>
                                    <b-form-input size="sm" type="number" v-model="item.move_quantity" class="mt-1"></b-form-input>
                                </div>
                                <div class="mt-2 d-flex" style="gap: 8px;">
                                    <b-button size="sm" variant="secondary" @click="cancel_move(item)">Cancel</b-button>
                                    <b-button size="sm" variant="success" @click="submit_move_wrapper(item)">Submit</b-button>
                                </div>
                            </section>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import Requestor from '@/utilities/Requestor';
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';
    import ItemComments from '@/components/importables/ItemComments';

    import {
        BIconFunnelFill, BIconBoxSeam, BIconGraphUp, BIconCheckCircle,
        BIconClipboardCheck, BIconClockHistory, BIconBox, BIconPercent,
        BIconCashStack, BIconGeoAlt, BIconArrowRepeat, BIconTable,
        BIconTag, BIconDiagram3, BIconSearch, BIconEye, BIconEyeSlash,
        BIconDownload, BIconArrowLeftRight, BIconExclamationTriangle,
        BIconImage, BIconCheckCircleFill, BIconXCircleFill, BIconPencilSquare,
        BIconGrid3x3Gap, BIconListUl, BIconBoxArrowInDown, BIconCheck2,
        BIconX, BIconChevronRight
    } from "bootstrap-vue";

    export default {
        name: "InventoryByLocation",
        components: {
            flatPickr, Loading, ItemComments,
            BIconFunnelFill, BIconBoxSeam, BIconGraphUp, BIconCheckCircle,
            BIconClipboardCheck, BIconClockHistory, BIconBox, BIconPercent,
            BIconCashStack, BIconGeoAlt, BIconArrowRepeat, BIconTable,
            BIconTag, BIconDiagram3, BIconSearch, BIconEye, BIconEyeSlash,
            BIconDownload, BIconArrowLeftRight, BIconExclamationTriangle,
            BIconImage, BIconCheckCircleFill, BIconXCircleFill, BIconPencilSquare,
            BIconGrid3x3Gap, BIconListUl, BIconBoxArrowInDown, BIconCheck2,
            BIconX, BIconChevronRight
        },
        data() {
            return {
                items: [],
                displayedItems: [],
                view_mode: 'product',
                activeSortKey: null,
                newDamageItem: {
                    source_locsub: null, source_sublocation_id: null,
                    target_locsub: null, target_sublocation_id: null,
                    damage_type: '', damage_grade: 'moderate',
                    damage_description: '', original_price: null,
                    selling_price: null, quantity: 1
                },
                damageImages: [],
                availableLocations: [],
                imagePreviews: [],
                damageTypeOptions: [
                    { value: 'customer_return', text: 'Customer Return' },
                    { value: 'shipping', text: 'Shipping Damage' },
                    { value: 'warehouse_handling', text: 'Warehouse Handling' },
                    { value: 'vendor_fault', text: 'Vendor Fault' },
                    { value: 'quality_control', text: 'Quality Control' },
                    { value: 'other', text: 'Other' }
                ],
                damageGradeOptions: [
                    { value: 'light', text: 'Light Damage' },
                    { value: 'moderate', text: 'Moderate Damage' },
                    { value: 'heavy', text: 'Heavy Damage' }
                ],
                locations: [],
                location_id: null,
                packet: {},
                items_being_loaded: false,
                picked_locsub: null,
                sort_asc: false,
                hide_zero_in_stock: false,
                search_txt: "",
                picked_category: null,
                categoryOptions: [],
                reservedInvoices: [],
                reservedInvoiceFields: [
                    { key: 'invoice_id', label: 'INVOICE ID', sortable: true },
                    { key: 'reserved_quantity', label: 'RESERVED QTY', sortable: true },
                    { key: 'invoice_date', label: 'INVOICE DATE', sortable: true },
                    { key: 'invoice_completed', label: 'STATUS', sortable: true }
                ],
                currentFullSizeImage: null,
                currentImageProductName: null,
                currentImageProductSku: null,
                selectedItem: null,
                receivedHistory: [],
                missingHistory: [],
                damagesHistory: [],
                soldHistory: [],
                newReceivedQty: 0, newMissingQty: 0, newDamagesQty: 0, newSoldQty: 0,
                editReceivedQty: 0, editMissingQty: 0, editDamagesQty: 0, editSoldQty: 0,
                historyFields: [
                    { key: 'timestamp', label: 'DATE', sortable: true },
                    { key: 'changed_by', label: 'BY', sortable: true },
                    { key: 'quantity', label: 'QTY', sortable: true },
                    { key: 'sublocation_name', label: 'LOC', sortable: true },
                    { key: 'source', label: 'SOURCE', sortable: true },
                    { key: 'actions', label: 'ACTION' }
                ],
                filtersVisible: false,
                isMobile: false,
                isSubmitting: false,
            }
        },
        watch: {
            items(newItems) {
                let categories = [...new Set(newItems.map(i => i.category).filter(Boolean))];
                this.categoryOptions = categories.map(c => ({ value: c, text: c }));
            },
            picked_locsub() { this.updateDisplayedItems(); },
            picked_category() { this.updateDisplayedItems(); },
            hide_zero_in_stock() { this.updateDisplayedItems(); },
            search_txt() { this.updateDisplayedItems(); }
        },
        computed: {
            canMove() {
                const r = this.packet.userDetails && this.packet.userDetails.userrole;
                return ['admin','accountmanager','officemanager','officeuser','warehousemanager','locationadmin'].includes(r);
            },
            canAsIs() {
                const r = this.packet.userDetails && this.packet.userDetails.userrole;
                return ['admin','officemanager'].includes(r);
            },
            listGridStyle() {
                return {
                    '--pc-cols': '250px minmax(0, 1.9fr) minmax(0, 1.5fr) minmax(0, 1.6fr) minmax(0, 1.4fr) 40px'
                };
            },
            locationName() {
                if (!this.location_id || !this.packet.locations) return null;
                const loc = this.packet.locations.find(l => l.value == this.location_id);
                return loc ? loc.text : null;
            },
            totalInventoryValue() {
                return this.displayedItems.filter(item => this.allowed(item)).reduce((sum, item) => sum + (parseFloat(item.selling_price || 0) * parseInt(item.in_stock || 0)), 0);
            },
            totalInStock() {
                return this.displayedItems.filter(item => this.allowed(item)).reduce((sum, item) => sum + parseInt(item.in_stock || 0), 0);
            },
            totalReserved() {
                return this.displayedItems.filter(item => this.allowed(item)).reduce((sum, item) => sum + parseInt(item.reserved || 0), 0);
            },
            totalAvailable() {
                return this.displayedItems.filter(item => this.allowed(item)).reduce((sum, item) => sum + parseInt(item.available || 0), 0);
            },
            stockHealthPercent() {
                const total = this.totalInStock + this.totalReserved;
                if (total === 0) return 100;
                return ((this.totalAvailable / total) * 100).toFixed(1);
            },
            availableLocations() {
                const uniqueLocations = [];
                const seenLocations = new Set();
                this.packet.locsubs.forEach(loc => {
                    let locParts = loc.value.split("|");
                    let locationId = locParts[0];
                    let locationName = loc.text.split(' - ')[0];
                    if (!seenLocations.has(locationId)) {
                        seenLocations.add(locationId);
                        uniqueLocations.push({ value: locationId, text: locationName });
                    }
                });
                return uniqueLocations;
            }
        },
        mounted() {
            this.handleResize();
            window.addEventListener("resize", this.handleResize);
        },
        beforeDestroy() { window.removeEventListener("resize", this.handleResize); },
        methods: {
            formatCurrency(value) {
                const num = parseFloat(value || 0);
                if (isNaN(num)) return '$0.00';
                return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
            },
            setViewMode(mode) {
                if (this.view_mode === mode) return;
                this.view_mode = mode;
                try { localStorage.setItem('inv_view_mode', mode); } catch (e) {}
            },
            invIsOpen(item) { return item._open === true; },
            invToggle(item) { this.$set(item, '_open', !this.invIsOpen(item)); },
            sortby(key) {
                if (this.activeSortKey === key) this.sort_asc = !this.sort_asc;
                else { this.activeSortKey = key; this.sort_asc = true; }
                const k = key;
                const asc = this.sort_asc;
                this.displayedItems = [...this.displayedItems].sort((a, b) => {
                    let av = a[k], bv = b[k];
                    if (['selling_price','in_stock','reserved','available','received','sold'].includes(k)) { av = parseFloat(av||0); bv = parseFloat(bv||0); }
                    else { av = (av||'').toString().toLowerCase(); bv = (bv||'').toString().toLowerCase(); }
                    if (av < bv) return asc ? -1 : 1;
                    if (av > bv) return asc ? 1 : -1;
                    return 0;
                });
            },
            handleResize() { this.isMobile = window.innerWidth < 768; },
            getProductImage(item) {
                if (!item) return null;
                let raw = item.image_url || item.product_image || item.image || null;
                if (!raw) return null;
                let imageUrl = String(raw).trim();
                if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') return null;
                if (imageUrl.includes('fmapi.myfurnituremecca.com')) return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                return imageUrl;
            },
            handleImageError(event, item) {
                event.target.style.display = 'none';
                const parent = event.target.parentElement;
                if (parent && !parent.querySelector('.product-image-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'product-image-fallback';
                    fallback.innerHTML = '<span style="font-size:12px;color:#94a3b8;">No Image</span>';
                    parent.appendChild(fallback);
                }
            },
            showFullInventoryImage(item) {
                if (!item) return;
                const imageUrl = this.getProductImage(item);
                if (!imageUrl) { this.$bvToast.toast("No image available", { title: "Info", autoHideDelay: 2000, variant: "info" }); return; }
                this.$bvModal.show('modal-inventory-image');
                this.$nextTick(() => {
                    this.currentFullSizeImage = imageUrl;
                    this.currentImageProductName = item.product_name || item.product_sku || 'Product Image';
                    this.currentImageProductSku = item.product_sku || '';
                });
            },
            async get_inventory_for_location() {
                this.items_being_loaded = true;
                try {
                    const response = await new Promise((resolve, reject) => {
                        Requestor.getSyncP("/api/inventory/get_inventory_for_location/", { location_id: this.location_id ? parseInt(this.location_id) : null }, this, resolve, reject);
                    });
                    if (response.data.error) {
                        this.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        return;
                    }
                    this.items = response.data.result.map(item => ({
                        ...item,
                        received: item.received || 0, missing: item.missing || 0,
                        damages: item.damages || 0, sold: item.sold || 0,
                        edit_received: false, edit_missing: false, edit_damages: false, edit_sold: false,
                        received_fake: item.received || 0, missing_fake: item.missing || 0,
                        damages_fake: item.damages || 0, sold_fake: item.sold || 0,
                        image_url: item.image_url || item.product_image || item.image || null
                    }));
                    this.updateDisplayedItems();
                } catch (error) {
                    console.error(error);
                    this.$bvToast.toast("Error retrieving inventory for location.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
                } finally {
                    this.items_being_loaded = false;
                }
            },
            updateDisplayedItems() { this.displayedItems = this.items.filter(item => this.allowed(item)); },
            show_add_to_damages(item) {
                this.selectedItem = item;
                this.newDamageItem.original_price = item.selling_price || null;
                this.newDamageItem.selling_price = Math.round((item.selling_price || 0) * 0.5 * 100) / 100;
                this.newDamageItem.quantity = 1;
                this.newDamageItem.damage_type = 'warehouse_handling';
                this.newDamageItem.damage_grade = 'moderate';
                this.newDamageItem.source_locsub = item.locsub;
                this.newDamageItem.target_locsub = item.locsub;
                this.availableLocations = this.packet.locsubs.filter(loc => true);
                this.isSubmitting = false;
                this.$bvModal.show('modal-add-to-damages');
            },
            handleImageUpload(event) {
                const files = event.target.files;
                this.imagePreviews = [];
                for (let file of files) {
                    const reader = new FileReader();
                    reader.onload = (e) => { this.imagePreviews.push(e.target.result); };
                    reader.readAsDataURL(file);
                }
            },
            getMaxAvailableQuantity() { return this.selectedItem ? this.selectedItem.in_stock : 0; },
            removeImage(index) { this.imagePreviews.splice(index, 1); },
            reset_damages_form() {
                this.newDamageItem = {
                    source_locsub: null, source_sublocation_id: null, target_locsub: null, target_sublocation_id: null,
                    damage_type: '', damage_grade: 'moderate', damage_description: '', original_price: null, selling_price: null, quantity: 1
                };
                this.damageImages = []; this.imagePreviews = []; this.availableLocations = [];
                this.isSubmitting = false;
                this.$bvModal.hide('modal-add-to-damages');
            },
            save_damaged_item() {
                if (this.isSubmitting) return;
                if (!this.validate_damage_form()) return;
                this.isSubmitting = true;
                const data = {
                    product_id: this.selectedItem.product_id, product_sku: this.selectedItem.product_sku,
                    source_locsub: this.newDamageItem.source_locsub, target_locsub: this.newDamageItem.target_locsub,
                    quantity: this.newDamageItem.quantity, damage_type: this.newDamageItem.damage_type,
                    damage_grade: this.newDamageItem.damage_grade, damage_description: this.newDamageItem.damage_description,
                    original_price: this.newDamageItem.original_price, selling_price: this.newDamageItem.selling_price,
                    images: this.imagePreviews, username: this.packet.userDetails.username
                };
                const that = this;
                Requestor.postAsyncP("/api/inventory/add_to_damages/", data, this,
                    (response) => {
                        that.isSubmitting = false;
                        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        else {
                            that.$bvToast.toast("ITEM ADDED TO DAMAGES SUCCESSFULLY!", { title: "Success", autoHideDelay: 1500, variant: "success" });
                            that.reset_damages_form();
                            that.get_inventory_for_location();
                        }
                    },
                    () => { that.isSubmitting = false; that.$bvToast.toast("Error adding item.", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
                );
            },
            validate_damage_form() {
                if (this.newDamageItem.source_locsub === this.newDamageItem.target_locsub) { this.$vex.dialog.alert({ message: "Source and target locations cannot be the same." }); return false; }
                if (this.newDamageItem.quantity > this.getMaxAvailableQuantity()) { this.$vex.dialog.alert({ message: `Cannot mark more than available. Available: ${this.getMaxAvailableQuantity()}` }); return false; }
                if (!this.newDamageItem.damage_type) { this.$vex.dialog.alert({ message: "Please select a damage type" }); return false; }
                if (!this.newDamageItem.selling_price) { this.$vex.dialog.alert({ message: "Please enter a selling price" }); return false; }
                return true;
            },
            show_reserved_invoices(item) {
                this.selectedItem = item;
                this.reservedInvoices = (item.invoices && item.invoices.length > 0) ? item.invoices : [];
                this.$bvModal.show('modal-reserved-invoices');
            },
            formatDate(dateString) { if (!dateString) return ''; return moment(dateString).format('DD-MM-YYYY'); },
            allowed(row) {
                let status = true;
                if (this.picked_locsub != null && row.locsub != this.picked_locsub) status = false;
                if (this.picked_category != null && row.category != this.picked_category) status = false;
                if (this.hide_zero_in_stock && row.in_stock == 0) status = false;
                if (this.search_txt && this.search_txt.length > 0) {
                    const q = this.search_txt.toLowerCase();
                    status = (row.product_name||'').toLowerCase().indexOf(q) > -1 || (row.product_sku||'').toLowerCase().indexOf(q) > -1;
                }
                return status;
            },
            downloadExcel() {
                const filtered = this.displayedItems.filter(row => this.allowed(row));
                if (filtered.length === 0) { alert("No data to export!"); return; }
                const ws_data = filtered.map(item => ({
                    Product: item.product_name, SKU: item.product_sku, Category: item.category,
                    Sublocation: this.packet.locsubsdict[item.locsub],
                    InStock: item.in_stock, Reserved: item.reserved, Available: item.available,
                    Received: item.received || 0, Missing: item.missing || 0, Damages: item.damages || 0, Sold: item.sold || 0,
                    SellingPrice: item.selling_price, WholesalePrice: item.wholesale_price, LandedCost: item.landed_cost,
                }));
                const escapeCSV = (value) => {
                    if (value === null || value === undefined) return "";
                    value = value.toString();
                    if (/[",\n]/.test(value)) value = '"' + value.replace(/"/g, '""') + '"';
                    return value;
                };
                const headers = Object.keys(ws_data[0]).map(escapeCSV).join(",");
                const rows = ws_data.map(obj => Object.values(obj).map(escapeCSV).join(",")).join("\n");
                const csvContent = "data:text/csv;charset=utf-8," + headers + "\n" + rows;
                const link = document.createElement("a");
                link.setAttribute("href", encodeURI(csvContent));
                link.setAttribute("download", "inventory_export.csv");
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
            },
            submit_move_wrapper(item) {
                if (!item.to_locsub) { this.$vex.dialog.alert({ message: "Please select a target location." }); return; }
                if (item.locsub == item.to_locsub) { this.$vex.dialog.alert({ message: "Cannot move into the same location." }); return; }
                item.move_quantity = parseInt(item.move_quantity);
                item.in_stock = parseInt(item.in_stock);
                if (isNaN(item.move_quantity)) { this.$vex.dialog.alert({ message: "Please enter a valid quantity." }); return; }
                if (item.move_quantity > item.in_stock) { this.$vex.dialog.alert({ message: "Cannot move more than available." }); return; }
                this.submit_move(item);
            },
            submit_move(item) {
                const that = this;
                this.items_being_loaded = true;
                Requestor.postSyncP("/api/inventory/move_inventory/", {
                    product_id: item.product_id, product_sku: item.product_sku,
                    from_locsub: item.locsub, to_locsub: item.to_locsub,
                    move_quantity: item.move_quantity, username: this.packet.userDetails.username,
                }, this,
                    (response) => {
                        if (response.data.result) { that.$bvToast.toast("MOVE SUCCESSFUL!", { title: "Success", autoHideDelay: 1500, variant: "success" }); that.get_inventory_for_location(); }
                        else that.$bvToast.toast("ERROR MOVING INVENTORY", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        that.items_being_loaded = false;
                    },
                    () => { that.$bvToast.toast("Error moving inventory.", { title: "Error", autoHideDelay: 1500, variant: "danger" }); that.items_being_loaded = false; }
                );
            },
            cancel_move(item) { item.move_items = false; },
            move_items(item) { item.move_items = true; },
            edit_in_stock(item) { item.edit_in_stock = true; item.in_stock_fake = item.in_stock; },
            cancel_save_in_stock(item) { item.edit_in_stock = false; },
            save_in_stock(item) {
                const splitted = item.locsub.split("|");
                const location_id = parseInt(splitted[0]);
                const sublocation_id = parseInt(splitted[1]);
                const nitems = parseInt(item.in_stock_fake);
                if (isNaN(nitems)) { this.$vex.dialog.alert({ message: "Please specify a valid quantity." }); return; }
                const that = this;
                Requestor.postAsyncP("/api/inventory/update_inventory/",
                    { product_sku: item.product_sku, location_id, sublocation_id, nitems }, this,
                    (response) => {
                        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        else { that.$bvToast.toast("UPDATED PRODUCT INVENTORY.", { title: "Success", autoHideDelay: 1500, variant: "success" }); item.edit_in_stock = false; item.in_stock = nitems; }
                    },
                    () => { that.$bvToast.toast("Error updating inventory.", { title: "Error", autoHideDelay: 1500, variant: "danger" }); item.edit_in_stock = false; }
                );
            },
            show_received_history(item) { this.selectedItem = item; this.fetchHistory('received'); this.$bvModal.show('modal-received-history'); },
            show_missing_history(item) { this.selectedItem = item; this.fetchHistory('missing'); this.$bvModal.show('modal-missing-history'); },
            show_damages_history(item) { this.selectedItem = item; this.fetchHistory('damages'); this.$bvModal.show('modal-damages-history'); },
            show_sold_history(item) { this.selectedItem = item; this.fetchHistory('sold'); this.$bvModal.show('modal-sold-history'); },
            fetchHistory(field) {
                if (!this.selectedItem) return;
                const splitted = this.selectedItem.locsub.split("|");
                const location_id = parseInt(splitted[0]);
                const sublocation_id = parseInt(splitted[1]);
                const that = this;
                Requestor.getSync("/api/inventory/get_inventory_history/",
                    { product_sku: this.selectedItem.product_sku, location_id, sublocation_id, field }, 
                    (response) => {
                        if (response.data.result) {
                            const enhanced = response.data.result.map(h => {
                                const sublocationKey = location_id + '|' + sublocation_id;
                                return { ...h, sublocation_name: that.packet.locsubsdict[sublocationKey] || 'Unknown' };
                            });
                            if (field === 'received') that.receivedHistory = enhanced;
                            else if (field === 'missing') that.missingHistory = enhanced;
                            else if (field === 'damages') that.damagesHistory = enhanced;
                            else if (field === 'sold') that.soldHistory = enhanced;
                        } else that.$bvToast.toast(response.data.error || "Error fetching history", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    },
                    () => that.$bvToast.toast("Error fetching history data.", { title: "Error", autoHideDelay: 1500, variant: "danger" })
                );
            },
            saveReceivedHistory() { this.saveHistory('received', this.newReceivedQty); },
            saveMissingHistory() { this.saveHistory('missing', this.newMissingQty); },
            saveDamagesHistory() { this.saveHistory('damages', this.newDamagesQty); },
            saveSoldHistory() { this.saveHistory('sold', this.newSoldQty); },
            saveHistory(field, quantity) {
                if (!this.selectedItem || !quantity || quantity <= 0) { this.$vex.dialog.alert({ message: "Please enter a valid quantity" }); return; }
                const splitted = this.selectedItem.locsub.split("|");
                const location_id = parseInt(splitted[0]);
                const sublocation_id = parseInt(splitted[1]);
                const that = this;
                Requestor.postAsyncP("/api/inventory/add_inventory_history/", {
                    product_sku: this.selectedItem.product_sku, location_id, sublocation_id,
                    field, quantity: parseInt(quantity), username: this.packet.userDetails.username, source: 'Manual Entry'
                }, this,
                    (response) => {
                        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        else {
                            that.$bvToast.toast(`ADDED ${field.toUpperCase()} HISTORY.`, { title: "Success", autoHideDelay: 1500, variant: "success" });
                            that.newReceivedQty = 0; that.newMissingQty = 0; that.newDamagesQty = 0; that.newSoldQty = 0;
                            that.fetchHistory(field);
                            that.get_inventory_for_location();
                        }
                    },
                    () => that.$bvToast.toast(`Error adding ${field} history.`, { title: "Error", autoHideDelay: 1500, variant: "danger" })
                );
            },
            deleteHistoryItem(field, historyId) {
                const that = this;
                Requestor.postAsyncP("/api/inventory/delete_inventory_history/", { history_id: historyId }, this,
                    (response) => {
                        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        else { that.$bvToast.toast("DELETED HISTORY ITEM.", { title: "Success", autoHideDelay: 1500, variant: "success" }); that.fetchHistory(field); that.get_inventory_for_location(); }
                    },
                    () => that.$bvToast.toast("Error deleting history item.", { title: "Error", autoHideDelay: 1500, variant: "danger" })
                );
            },
        },
        created() {
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            if (this.packet.userDetails.properties && this.packet.userDetails.properties.location_id) {
                const lid = this.packet.userDetails.properties.location_id;
                this.locations = this.packet.locations.filter(d => d.value == lid || d.is_main_warehouse == true);
            } else {
                this.locations = this.packet.locations;
            }
            this.locations = this.locations.filter(d => d.text != 'All');
            this.locations = this.locations.sort((a, b) => a.is_main_warehouse - b.is_main_warehouse);
            const saved = localStorage.getItem('inv_view_mode');
            this.view_mode = (saved === 'product' || saved === 'list') ? saved : 'product';
        }
    }
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');

.dashboard-container.inv-page {
    max-width: 1800px; width: 97%; margin: 18px auto 60px; padding-bottom: 40px;
    font-family: 'Manrope', system-ui, sans-serif; color: #221F1C; font-size: 15px;
}

/* HERO */
.rsv2-hero {
    background: #221F1C; color: #fff; border-radius: 20px; overflow: hidden;
    border-bottom: 5px solid #EE7A22; margin-bottom: 14px;
    animation: rsv2SlideDown 0.5s ease-out;
}
@keyframes rsv2SlideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
.rsv2-hero-grid { display: grid; grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr); gap: 24px; padding: 18px 30px; align-items: center; }
.rsv2-hero-title {
    font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 800;
    margin: 0 0 14px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
}
.rsv2-count-pill {
    display: inline-flex; align-items: center; justify-content: center;
    background: #EE7A22; color: #fff; font-family: 'Sora', sans-serif;
    font-size: 17px; font-weight: 800; padding: 4px 16px; border-radius: 999px;
}
.rsv2-hero-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.rsv2-tag {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 13.5px; font-weight: 700; padding: 6px 15px; border-radius: 999px;
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.9); border: 1px solid rgba(255,255,255,0.14);
}
.rsv2-tag.is-orange { background: rgba(238,122,34,0.22); color: #FFB06B; border-color: rgba(238,122,34,0.45); }
.rsv2-tag.is-teal { background: rgba(15,110,107,0.24); color: #7FDACF; border-color: rgba(15,110,107,0.5); }
.rsv2-hero-qr {
    display: flex; align-items: center; gap: 16px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
    border-radius: 16px; padding: 14px 20px; color: #FFB06B;
}
.rsv2-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 16px; color: #fff; }
.rsv2-qr-sub { font-size: 13.5px; color: rgba(255,255,255,0.65); margin-top: 2px; }
@media (max-width: 900px) { .rsv2-hero-grid { grid-template-columns: 1fr; gap: 14px; } .rsv2-hero-title { font-size: 20px; } }

/* VIEW TOGGLE */
.rsv2-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; padding: 14px 18px; margin-bottom: 12px; }
.rsv2-viewcard { padding: 16px 20px; }
.rsv2-view-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; }
.rsv2-view-block { display: flex; flex-direction: column; gap: 8px; }
.rsv2-label { font-size: 12px; font-weight: 800; letter-spacing: 0.08em; color: #736C63; text-transform: uppercase; display: inline-flex; align-items: center; gap: 6px; }
.rsv2-pillgroup { display: inline-flex; background: #F1EEE9; border-radius: 12px; padding: 4px; gap: 4px; }
.rsv2-pill-btn { border: 0; background: transparent; padding: 9px 18px; border-radius: 9px; font-weight: 700; font-size: 14px; color: #4A4540; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.15s ease; }
.rsv2-pill-btn:hover { color: #EE7A22; }
.rsv2-pill-btn.is-active { background: #EE7A22; color: #fff; box-shadow: 0 4px 12px -6px rgba(238,122,34,0.6); }
.inv-refresh-btn { border-radius: 10px; font-weight: 700; border: 1.5px solid #E5E1DA; color: #221F1C; background: #fff; }
.inv-refresh-btn:hover { border-color: #EE7A22; color: #D4620F; }

/* KPI */
.rsv2-kpi-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 14px; }
.rsv2-kpi { display: flex; align-items: center; gap: 14px; background: #fff; border: 1px solid #E5E1DA; border-left: 6px solid transparent; border-radius: 14px; padding: 14px 16px; transition: transform 0.2s ease, box-shadow 0.2s ease; }
.rsv2-kpi:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -16px rgba(34,31,28,0.4); }
.rsv2-kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; flex-shrink: 0; }
.rsv2-kpi-body { display: flex; flex-direction: column; min-width: 0; }
.rsv2-kpi-value { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 800; line-height: 1.1; letter-spacing: -0.01em; }
.rsv2-kpi-label { font-size: 13.5px; color: #736C63; font-weight: 600; margin-top: 2px; }
.rsv2-kpi.is-total { border-left-color: #D4620F; } .rsv2-kpi.is-total .rsv2-kpi-icon { background: linear-gradient(135deg, #EE7A22, #D4620F); } .rsv2-kpi.is-total .rsv2-kpi-value { color: #D4620F; }
.rsv2-kpi.is-refund { border-left-color: #C93C30; } .rsv2-kpi.is-refund .rsv2-kpi-icon { background: linear-gradient(135deg, #C93C30, #A22A1F); } .rsv2-kpi.is-refund .rsv2-kpi-value { color: #C93C30; }
.rsv2-kpi.is-count { border-left-color: #0F6E6B; } .rsv2-kpi.is-count .rsv2-kpi-icon { background: linear-gradient(135deg, #0F6E6B, #0B5552); } .rsv2-kpi.is-count .rsv2-kpi-value { color: #0F6E6B; }
.rsv2-kpi.is-layaway { border-left-color: #C98A12; } .rsv2-kpi.is-layaway .rsv2-kpi-icon { background: linear-gradient(135deg, #E0A524, #C98A12); } .rsv2-kpi.is-layaway .rsv2-kpi-value { color: #C98A12; }
@media (max-width: 991px) { .rsv2-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .rsv2-kpi-grid { grid-template-columns: 1fr; } }

/* FILTERS */
.inv-filters-card { border: 1px solid #E5E1DA !important; border-radius: 16px !important; background: #fff !important; box-shadow: none !important; }
.filter-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 16px; }
.filter-item { min-width: 0; }
.filter-label { font-size: 11.5px; font-weight: 800; color: #736C63; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 8px; display: block; }
.filter-select, .filter-input { border-radius: 10px; border: 1.5px solid #E5E1DA; font-size: 14px; padding: 9px 12px; background: #F7F5F2; height: 44px; font-weight: 600; color: #221F1C; }
.filter-select:focus, .filter-input:focus { border-color: #EE7A22; box-shadow: 0 0 0 3px rgba(238,122,34,0.15); background: #fff; }
.toggle-btn, .download-btn { border-radius: 10px; height: 44px; font-weight: 700; }

/* LOADING / EMPTY */
.rsv2-loading-inline { display: flex; align-items: center; justify-content: center; gap: 12px; color: #736C63; font-weight: 600; padding: 40px 20px; }
.rsv2-empty { text-align: center; padding: 60px 30px; border: 2px dashed #D6CFC4; background: #FAF9F7; }
.rsv2-empty-icon { width: 78px; height: 78px; border-radius: 22px; background: #FFF1E4; color: #EE7A22; display: inline-flex; align-items: center; justify-content: center; font-size: 34px; margin-bottom: 18px; }
.rsv2-empty h3 { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin: 0 0 8px; }
.rsv2-empty p { color: #736C63; max-width: 50ch; margin: 0 auto; }

/* SECTION HEADER */
.products-header { display: flex; justify-content: space-between; align-items: baseline; margin: 6px 0 14px; flex-wrap: wrap; gap: 12px; }
.section-title { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800; color: #221F1C; margin: 0; letter-spacing: -0.4px; }
.section-subtitle { font-size: 14px; color: #736C63; font-weight: 600; }

/* =====================================================
   PRODUCT CARD VIEW
   ===================================================== */
.products-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin-bottom: 24px; }
@media (max-width: 1400px) { .products-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .products-grid { grid-template-columns: 1fr; } }

.pc-card {
    position: relative; background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.pc-card:hover { border-color: #D9D1C5; box-shadow: 0 8px 22px -16px rgba(34,31,28,0.4); transform: translateY(-2px); }

.inv-pc-sum { display: flex; flex-direction: column; padding: 16px; gap: 14px; }
.inv-pc-img { position: relative; width: 100%; height: 220px; border-radius: 12px; background: linear-gradient(160deg, #F7F3EE, #EDE8E1); overflow: hidden; cursor: zoom-in; }
.pc-img-btn { position: absolute; inset: 0; width: 100%; height: 100%; padding: 8px; border: 0; background: transparent; }
.pc-img-btn img { width: 100%; height: 100%; object-fit: contain; display: block; }
.pc-img-empty { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #B9B1A6; font-size: 14px; }
.pc-prod { display: flex; flex-direction: column; gap: 6px; }
.pc-name { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; line-height: 1.35; color: #221F1C; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 44px; }
.pc-prod-meta { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.pc-sku { font-size: 13px; font-weight: 600; color: #736C63; }
.pc-sku b { color: #34302B; font-weight: 800; }
.pc-badges { display: flex; flex-wrap: wrap; gap: 5px; }
.pc-badge { display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 11.5px; font-weight: 800; line-height: 1.4; background: #fff; border: 1px solid #E5E1DA; color: #221F1C; white-space: nowrap; }
.pc-badge.is-dark { background: #221F1C; border-color: #221F1C; color: #fff; }
.pc-badge.is-good { background: #E2F2F0; border-color: #BFE1DD; color: #0F6E6B; }
.pc-badge.is-danger { background: #FBE9E7; border-color: #F3C9C3; color: #C93C30; }
.pc-badge.is-warn { background: #FDF3DE; border-color: #F1DDAE; color: #9A6A0C; }
.pc-prod-taxonomy { display: flex; flex-direction: column; gap: 4px; margin-top: 2px; font-size: 13px; font-weight: 600; color: #736C63; }
.pc-tax-item { display: inline-flex; align-items: center; gap: 4px; }
.pc-tax-item .b-icon { color: #EE7A22; font-size: 12px; flex-shrink: 0; }

.inv-pc-price { padding: 12px 14px; border-radius: 12px; background: #FFF1E4; border: 1px solid #F5CFAE; }
.pc-sum-k { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #736C63; margin-bottom: 4px; }
.inv-pc-price .pc-sum-k { color: #D4620F; }
.pc-big { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 800; line-height: 1.15; font-variant-numeric: tabular-nums; }
.pc-big.is-price { color: #D4620F; }
.pc-sum-v { font-size: 15px; font-weight: 700; color: #221F1C; }

.inv-pc-stock { padding: 12px 14px; background: #F7F5F2; border-radius: 12px; border: 1px solid #E5E1DA; }
.inv-stock-rows { display: flex; flex-direction: column; gap: 6px; }
.inv-stock-row { display: flex; align-items: center; gap: 10px; padding: 6px 8px; border-radius: 8px; border: 0; background: transparent; text-align: left; font: inherit; color: inherit; cursor: default; transition: background 0.12s ease; }
button.inv-stock-row { cursor: pointer; }
button.inv-stock-row:hover { background: #FFF1E4; }
.inv-stock-ico { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 7px; font-size: 13px; }
.inv-stock-ico.is-res { background: #FFF1E4; color: #EE7A22; }
.inv-stock-ico.is-av { background: #E2F2F0; color: #0F6E6B; }
.inv-stock-ico.is-in { background: #E2EFF7; color: #1F6690; }
.inv-stock-k { flex: 1 1 auto; font-size: 13px; font-weight: 700; color: #34302B; }
.inv-stock-v { font-family: 'Sora', sans-serif; font-size: 17px; font-weight: 800; font-variant-numeric: tabular-nums; color: #221F1C; }

.inv-pc-actions { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding-top: 8px; border-top: 1px dashed #E5E1DA; }
.pc-icon-btn { width: 36px; height: 36px; padding: 0; border: 1.5px solid #E5E1DA; border-radius: 9px; background: #fff; color: #34302B; font-size: 15px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.pc-icon-btn:hover { border-color: #EE7A22; color: #D4620F; }
.pc-icon-btn.is-danger { background: #C93C30; border-color: #C93C30; color: #fff; }
.pc-icon-btn.is-danger:hover { background: #A22A1F; border-color: #A22A1F; }
.inv-move-btn, .inv-asis-btn { flex: 1 1 auto; border-radius: 9px; font-weight: 700; font-size: 13px; padding: 7px 10px; }
.inv-move-btn { border: 1.5px solid #E5E1DA !important; color: #34302B !important; background: #fff !important; }
.inv-move-btn:hover { border-color: #EE7A22 !important; color: #D4620F !important; }
.inv-asis-btn { background: #221F1C !important; border-color: #221F1C !important; color: #fff !important; }
.inv-asis-btn:hover { background: #0F6E6B !important; border-color: #0F6E6B !important; }
.inv-move-form { background: #F7F5F2; border: 1px solid #E5E1DA; border-radius: 12px; padding: 12px; margin-top: 4px; }
.move-row { font-size: 13.5px; margin-bottom: 8px; font-weight: 600; color: #34302B; }
.move-actions { display: flex; gap: 8px; margin-top: 8px; }

/* =====================================================
   LIST VIEW (exact Product Manager layout)
   ===================================================== */
.pc-list {
    --pc-ink: #221F1C; --pc-ink-2: #34302B;
    --pc-orange: #EE7A22; --pc-orange-dark: #D4620F; --pc-orange-soft: #FFF1E4;
    --pc-teal: #0F6E6B; --pc-teal-soft: #E2F2F0;
    --pc-red: #C93C30; --pc-red-soft: #FBE9E7;
    --pc-warn: #9A6A0C; --pc-warn-soft: #FDF3DE;
    --pc-panel: #F7F5F2; --pc-line: #E5E1DA; --pc-muted: #736C63;
    font-family: 'Manrope', system-ui, sans-serif;
    color: var(--pc-ink);
}
.inv-pc-list { padding: 0 2px 16px; }
.pc-ico { display: inline-block !important; width: 1em !important; height: 1em !important; flex-shrink: 0; vertical-align: -0.125em; line-height: 1; }
.pc-ico >>> svg { display: block !important; width: 1em !important; height: 1em !important; }
.pc-list >>> svg.b-icon { width: 1em; height: 1em; vertical-align: -0.125em; }

.pc-head {
    position: sticky; top: 0; z-index: 7;
    display: grid; grid-template-columns: var(--pc-cols);
    align-items: center; column-gap: 16px;
    padding: 0 15px 0 19px; margin-bottom: 8px;
    height: 40px; background: var(--pc-ink); border-radius: 12px;
}
.pc-head-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pc-head-cell.is-num { justify-content: flex-start; }
.pc-head-sort {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 0; border: 0; background: transparent;
    color: rgba(255,255,255,0.72); font-size: 11.5px; font-weight: 800;
    letter-spacing: 0.06em; text-transform: uppercase;
    cursor: pointer; white-space: nowrap;
}
.pc-head-sort:hover { color: #fff; }
.pc-head-static {
    display: inline-flex; align-items: center;
    color: rgba(255,255,255,0.72); font-size: 11.5px; font-weight: 800;
    letter-spacing: 0.06em; text-transform: uppercase; white-space: nowrap;
}

.inv-pc-card {
    position: relative; margin-bottom: 10px;
    background: #fff; border: 1px solid var(--pc-line); border-radius: 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.inv-pc-card::before {
    content: ''; position: absolute; left: -1px; top: 14px;
    width: 4px; height: 56px; border-radius: 0 4px 4px 0;
    background: var(--pc-teal);
}
.inv-pc-card.is-low::before { background: #E0A21B; }
.inv-pc-card.is-out::before { background: var(--pc-red); }
.inv-pc-card:hover { border-color: #D9D1C5; box-shadow: 0 8px 22px -16px rgba(34,31,28,0.4); }
.inv-pc-card.is-open { border-color: var(--pc-orange); }

.pc-sum {
    display: grid; grid-template-columns: var(--pc-cols);
    align-items: start; column-gap: 16px;
    padding: 12px 14px 12px 18px;
}

.inv-pc-img-list {
        width: 230px;
    height: 160px;
    position: relative; border-radius: 12px;
    background: linear-gradient(160deg, #F7F3EE, #EDE8E1);
    overflow: hidden;
}
.inv-pc-img-list .pc-img-btn { position: absolute; inset: 0; width: 100%; height: 100%; padding: 6px; border: 0; background: transparent; }
.inv-pc-img-list .pc-img-btn img { width: 100%; height: 100%; object-fit: contain; display: block; }
.inv-pc-img-list .pc-img-empty { position: absolute; inset: 0; color: #B9B1A6; font-size: 22px; display: flex; align-items: center; justify-content: center; }

.pc-prod { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.pc-name { font-family: 'Sora', sans-serif; font-size: 15.5px; font-weight: 700; line-height: 1.3; color: var(--pc-ink); word-break: break-word; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pc-prod-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pc-sku { font-size: 14px; font-weight: 600; color: var(--pc-muted); }
.pc-sku b { color: var(--pc-ink-2); font-weight: 700; }
.pc-badges { display: flex; flex-wrap: wrap; gap: 5px; }
.pc-badges:empty { display: none; }
.pc-badge { display: inline-block; padding: 2px 9px; border-radius: 99px; font-size: 11.5px; font-weight: 800; line-height: 1.4; background: #fff; border: 1px solid var(--pc-line); color: var(--pc-ink); white-space: nowrap; }
.pc-badge.is-dark { background: var(--pc-ink); border-color: var(--pc-ink); color: #fff; }
.pc-badge.is-good { background: var(--pc-teal-soft); border-color: #BFE1DD; color: var(--pc-teal); }
.pc-badge.is-danger { background: var(--pc-red-soft); border-color: #F3C9C3; color: var(--pc-red); }
.pc-badge.is-warn { background: var(--pc-warn-soft); border-color: #F1DDAE; color: var(--pc-warn); }
.pc-prod-taxonomy { display: flex; flex-direction: column; gap: 4px; margin-top: 2px; font-size: 13px; font-weight: 600; color: var(--pc-muted); }
.pc-tax-item { display: inline-flex; align-items: center; gap: 4px; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pc-tax-item > .pc-ico { font-size: 12px; color: var(--pc-orange); flex-shrink: 0; }

.pc-sum-cell { min-width: 0; }
.pc-sum-k { font-size: 11.5px; font-weight: 700; letter-spacing: 0.03em; color: var(--pc-muted); text-transform: uppercase; }
.pc-sum-v { font-size: 14px; font-weight: 700; color: var(--pc-ink); margin-bottom: 4px; }
.pc-big { font-family: 'Sora', sans-serif; font-size: 21px; font-weight: 800; line-height: 1.2; font-variant-numeric: tabular-nums; color: var(--pc-ink); }
.pc-big.is-price { color: var(--pc-orange-dark); }
.inv-list-price-cell { padding-top: 4px; }

/* Stock cell — reuse Product Manager stock style */
.pc-sum-stock {
    display: flex; flex-direction: column; gap: 6px; padding: 8px;
    border-radius: 10px; background: var(--pc-panel);
    border: 1px solid var(--pc-line); min-width: 0;
}
.pc-stock-head {
    display: flex; align-items: center; gap: 8px;
    padding: 0 6px 6px; margin-bottom: 2px;
    border-bottom: 1px dashed var(--pc-line);
}
.pc-stock-title { flex: 1 1 auto; min-width: 0; font-size: 10.5px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--pc-muted); }
.pc-stock-row {
    display: flex; align-items: center; gap: 8px;
    padding: 4px 6px; border-radius: 7px;
    font-size: 12.5px; line-height: 1.3;
}
.pc-stock-k { flex: 1 1 auto; min-width: 0; font-size: 10.5px; font-weight: 800; letter-spacing: 0.04em; color: var(--pc-muted); text-transform: uppercase; white-space: nowrap; }
.pc-stock-v { flex: 0 0 auto; font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--pc-ink-2); text-align: right; }
.pc-stock-v.is-all { color: var(--pc-ink); font-size: 13.5px; }
.pc-stock-v.is-available { color: var(--pc-teal); }

/* Totals cell */
.pc-sum-totals {
    display: flex; flex-direction: column; gap: 5px; padding: 8px;
    border-radius: 10px; background: var(--pc-panel);
    border: 1px solid var(--pc-line); min-width: 0;
}
.pc-totals-head { display: flex; align-items: baseline; justify-content: space-between; gap: 6px; padding: 0 6px 6px; margin-bottom: 2px; border-bottom: 1px dashed var(--pc-line); }
.pc-totals-title { font-size: 10.5px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--pc-muted); }
.pc-totals-hint { font-size: 10px; font-weight: 600; color: var(--pc-muted); opacity: 0.8; white-space: nowrap; }
.pc-totals-row {
    display: flex; align-items: center; gap: 8px;
    width: 100%; padding: 5px 6px; border: 0;
    border-radius: 7px; background: transparent;
    font: inherit; color: inherit; text-align: left;
    cursor: pointer; transition: background 0.12s ease;
}
.pc-totals-row:hover:not(:disabled) { background: var(--pc-orange-soft); }
.pc-totals-row:hover:not(:disabled) .pc-totals-arrow { opacity: 1; transform: translateX(2px); color: var(--pc-orange-dark); }
.pc-totals-ico { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 24px; height: 24px; border-radius: 7px; font-size: 12px; }
.pc-totals-ico.is-ord { background: #E2EFF7; color: #1F6690; }
.pc-totals-ico.is-recv { background: var(--pc-teal-soft); color: var(--pc-teal); }
.pc-totals-ico.is-sold { background: var(--pc-warn-soft); color: #B07A06; }
.pc-totals-k { flex: 1 1 auto; min-width: 0; font-size: 13px; font-weight: 700; color: var(--pc-ink-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pc-totals-v { flex-shrink: 0; min-width: 2ch; font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; font-variant-numeric: tabular-nums; text-align: right; }
.pc-totals-v.is-ord { color: #1F6690; }
.pc-totals-v.is-recv { color: var(--pc-teal); }
.pc-totals-v.is-sold { color: #B07A06; }
.pc-totals-arrow { flex-shrink: 0; font-size: 11px; color: var(--pc-muted); opacity: 0.35; transition: opacity 0.12s ease, transform 0.12s ease; }

/* Actions cell */
.pc-sum-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; flex-wrap: wrap; }
.pc-sum-actions .pc-icon-btn {
    position: relative; width: 34px; height: 34px; padding: 0 !important;
    border: 1px solid var(--pc-line) !important;
    border-radius: 9px !important; background: #fff !important;
    color: var(--pc-ink-2) !important; font-size: 15px;
    cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
}
.pc-sum-actions .pc-icon-btn:hover { border-color: var(--pc-orange) !important; color: var(--pc-orange-dark) !important; }
.pc-sum-actions .pc-icon-btn.is-danger { background: var(--pc-red) !important; border-color: var(--pc-red) !important; color: #fff !important; }
.pc-sum-actions .pc-icon-btn.is-danger:hover { background: #A22A1F !important; border-color: #A22A1F !important; }
.pc-sum-actions .pc-toggle { background: var(--pc-panel) !important; }
.inv-pc-card.is-open .pc-toggle { background: var(--pc-ink) !important; border-color: var(--pc-ink) !important; color: #fff !important; }

/* Details */
.pc-details {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 10px; padding: 12px 14px 14px 18px;
    border-top: 1px solid var(--pc-line);
    background: #FCFBF9; border-radius: 0 0 14px 14px;
}
.pc-sec { min-width: 0; padding: 10px 14px 6px; background: #fff; border: 1px solid var(--pc-line); border-radius: 12px; }
.pc-sec-title { display: flex; align-items: baseline; justify-content: space-between; margin: 0 0 4px; padding-bottom: 6px; border-bottom: 2px solid var(--pc-orange); font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 700; color: var(--pc-ink); }
.pc-sec-title small { font-family: 'Manrope', sans-serif; font-size: 11px; font-weight: 600; color: var(--pc-muted); }
.pc-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 6px 0; border-bottom: 1px dashed var(--pc-line); font-size: 13.5px; }
.pc-row:last-child { border-bottom: 0; }
.pc-k { color: var(--pc-muted); font-weight: 600; white-space: nowrap; flex-shrink: 0; }
.pc-v { min-width: 0; font-weight: 700; color: var(--pc-ink); text-align: right; font-variant-numeric: tabular-nums; word-break: break-word; }
.is-good { color: var(--pc-teal) !important; }
.is-bad { color: var(--pc-red) !important; }
.pc-row--btn { width: calc(100% + 12px); margin: 0 -6px; padding: 6px; border: 0; border-bottom: 1px dashed var(--pc-line); border-radius: 6px; background: transparent; text-align: left; cursor: pointer; }
.pc-row--btn:last-child { border-bottom: 0; }
.pc-row--btn:hover { background: var(--pc-orange-soft); }

/* Responsive list */
@media (max-width: 1199.98px) {
    .pc-head { display: none; }
    .pc-sum {
        grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) 40px;
        grid-template-areas:
            "img  prod  prod  act"
            "price price stock stock"
            "totals totals totals totals";
        row-gap: 12px;
    }
    .inv-pc-img-list { grid-area: img; }
    .pc-prod { grid-area: prod; }
    .inv-list-price-cell { grid-area: price; }
    .pc-sum-stock { grid-area: stock; }
    .pc-sum-totals { grid-area: totals; }
    .pc-sum-actions { grid-area: act; align-self: start; }
}
@media (max-width: 767.98px) {
    .pc-sum { grid-template-columns: 64px minmax(0, 1fr) auto; grid-template-areas: "img prod act" "price price price" "stock stock stock" "totals totals totals"; gap: 10px; }
    .inv-pc-img-list { width: 64px; height: 64px; }
}

/* Responsive page */
@media (max-width: 992px) { .dashboard-container.inv-page { width: 100%; padding: 0 12px; } .filter-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 576px) { .filter-grid { grid-template-columns: 1fr; } .rsv2-hero-title { font-size: 18px; } }

/* Invoice link */
.invoice-link { color: #D4620F; text-decoration: none; font-weight: 700; padding: 4px 8px; border-radius: 4px; transition: all 0.2s; }
.invoice-link:hover { color: #EE7A22; background-color: #FFF1E4; text-decoration: none; }
</style>


// WEBPACK FOOTER //
// src/components/Inventory/InventoryByLocation.vue